// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/FiltroPeriodo.tsx
//
//  Período com atalhos Hoje / Semana / Mês / Personalizado (padrão do Desktop).
//  Trabalha com datas "yyyy-MM-dd" (dia local, sem fuso): mande assim para a API,
//  que converte para o intervalo UTC (FusoNegocio) quando a coluna é timestamptz.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { periodoDoAtalho, type AtalhoPeriodo, type Periodo } from "@/lib/consultas";

type Atalho = AtalhoPeriodo | "personalizado";

const ATALHOS: { valor: Atalho; rotulo: string }[] = [
  { valor: "todos", rotulo: "Qualquer data" },
  { valor: "hoje", rotulo: "Hoje" },
  { valor: "semana", rotulo: "Esta semana" },
  { valor: "mes", rotulo: "Este mês" },
  { valor: "personalizado", rotulo: "Personalizado" },
];

function atalhoDe({ inicio, fim }: Periodo): Atalho {
  if (!inicio && !fim) return "todos";
  for (const a of ["hoje", "semana", "mes"] as const) {
    const p = periodoDoAtalho(a);
    if (p.inicio === inicio && p.fim === fim) return a;
  }
  return "personalizado";
}

interface FiltroPeriodoProps {
  inicio: string;
  fim: string;
  onChange: (p: Periodo) => void;
  /** Ex.: "Cadastro", "Emissão", "Vencimento". */
  rotulo?: string;
  /** Esconde "Qualquer data" quando o período é obrigatório. */
  obrigatorio?: boolean;
  className?: string;
}

export function FiltroPeriodo({ inicio, fim, onChange, rotulo = "Período", obrigatorio, className }: FiltroPeriodoProps) {
  // "Personalizado" escolhido com datas iguais às de um atalho continua mostrando os campos.
  const [forcarPersonalizado, setForcarPersonalizado] = useState(false);
  // ("Limpar filtros" zera as datas e com isso desfaz o forçado.)
  const atalho: Atalho = forcarPersonalizado && (inicio || fim) ? "personalizado" : atalhoDe({ inicio, fim });
  const mostrarDatas = atalho === "personalizado";

  const escolher = (valor: string) => {
    const a = valor as Atalho;
    setForcarPersonalizado(a === "personalizado");
    if (a !== "personalizado") onChange(periodoDoAtalho(a));
    else if (!inicio && !fim) onChange(periodoDoAtalho("mes"));
  };

  return (
    <div className={cn("flex flex-wrap items-end gap-2", className)}>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{rotulo}</Label>
        <Select value={atalho} onValueChange={escolher}>
          <SelectTrigger className="h-9 w-[150px]" aria-label={rotulo}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ATALHOS.filter((a) => !(obrigatorio && a.valor === "todos")).map((a) => (
              <SelectItem key={a.valor} value={a.valor}>{a.rotulo}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {mostrarDatas && (
        <>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">De</Label>
            <Input
              type="date" className="h-9 w-[150px]" value={inicio} max={fim || undefined}
              aria-label={`${rotulo}: data inicial`}
              onChange={(e) => onChange({ inicio: e.target.value, fim })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Até</Label>
            <Input
              type="date" className="h-9 w-[150px]" value={fim} min={inicio || undefined}
              aria-label={`${rotulo}: data final`}
              onChange={(e) => onChange({ inicio, fim: e.target.value })}
            />
          </div>
        </>
      )}
    </div>
  );
}
